import type { GroupSettings } from '#/services/settings/schema.js'
import { clear, el, setError } from './dom.js'
import { logChannelIcon } from './icons.js'
import { t } from './labels.js'
import { sectionHead } from './sheet.js'

// The bespoke log-channel control (NOT a spec/form field). logChannelId can't flow through the generic
// PATCH /api/settings because setting it is SELF-VERIFYING — the bot must prove it can post to the channel
// BEFORE the id is saved, or the mirror would silently never work while the UI said "saved" (the broadcaster
// fails soft). So this control POSTs to the dedicated /api/log-channel endpoint (api.ts setLogChannel), which
// test-posts then saves. The silent/language precedent: a bespoke control, on WEBAPP_BESPOKE_KEYS, that the
// declarative spec can't model.
//
// Split like stats.ts/form.ts: PURE planLogChannel + validateChannelInput (unit-tested) + a thin
// renderLogChannel DOM transport (live-verify only). All text goes through el() → safe text nodes (XSS-safe).

export interface LogChannelModel { currentId: string | null }

/** PURE: the display model — the configured channel id, or null = none. Pins the control reads logChannelId. */
export function planLogChannel(settings: GroupSettings): LogChannelModel {
  return { currentId: settings.logChannelId }
}

/**
 * PURE: validate a typed channel id. An integer id (trimmed) that Number() carries exactly → set; anything
 * else (empty, @username, a pasted URL, a number past the safe-integer range) → invalid. Mirrors the server's
 * rule (isTelegramChatId, services/settings/schema.ts — not imported, to keep the schema module out of the
 * bundle) so the UI can reject junk WITHOUT a round-trip; the backend re-validates + test-posts regardless
 * (it stays the authority — this is pre-flight UX only). "off" is a dedicated Disable button, not a magic
 * word here.
 */
export type ChannelInput = { kind: 'set', id: string } | { kind: 'invalid' }
const CHANNEL_ID = /^-?\d+$/
export function validateChannelInput(raw: string): ChannelInput {
  const v = raw.trim()
  return CHANNEL_ID.test(v) && Number.isSafeInteger(Number(v)) ? { kind: 'set', id: v } : { kind: 'invalid' }
}

// ---- DOM transport (live-verify only) ----

export interface LogChannelHandlers {
  /** Save a validated channel id (POST → test-post → save). */
  onSet: (id: string) => void
  /** Clear the log channel (POST channelId=null). */
  onDisable: () => void
}

/**
 * Render the log-channel section into `root` (replacing its content — re-runs on every save/language switch):
 * the section head + guide, the current state, then a numeric-id input + Save (+ Disable when one is set).
 */
export function renderLogChannel(root: HTMLElement, model: LogChannelModel, handlers: LogChannelHandlers): void {
  clear(root)
  // The head skeleton (icon + title + info sheet) is the shared sectionHead — same as the stats section.
  root.append(sectionHead(logChannelIcon, 'ui-logchannel-title', 'ui-logchannel-body'))

  // Current state — the id is decoded in JS (the web string stays placeholder-free: the bot's
  // log-channel-current interpolates {$id}, which the web codegen forbids). Reuses the stats-caption sub-line.
  const current = model.currentId !== null ? `${t('ui-logchannel-current')} ${model.currentId}` : t('ui-logchannel-none')
  root.append(el('div', { class: 'stats-caption' }, current))

  // A channel id is a SIGNED integer (e.g. -1001234567890) — deliberately NO inputmode="numeric": the mobile
  // numeric keypad (iOS/Android, the primary Mini App runtime) has no minus key, so the leading '-' couldn't
  // be TYPED, only pasted (cold-eye fold). type="text" gives the full keyboard; validateChannelInput enforces
  // the shape on Save regardless.
  const input = el('input', {
    'type': 'text',
    'class': 'lc-input',
    'aria-label': t('ui-logchannel-title'),
    'placeholder': t('ui-logchannel-placeholder'),
  }) as HTMLInputElement

  const save = el('button', { class: 'lc-btn lc-save', type: 'button' }, t('ui-logchannel-save'))
  save.addEventListener('click', () => {
    const parsed = validateChannelInput(input.value)
    if (parsed.kind === 'invalid') {
      // Loud, precise, and NO write — the same "that isn't a channel id" refusal the command gives. Keeps
      // the typed text so the user can fix it; the backend never sees the junk.
      setError(t('ui-logchannel-invalid'))
      return
    }
    handlers.onSet(parsed.id)
  })

  const controls = el('div', { class: 'lc-controls' }, input, save)
  // Disable only when a channel is currently set (nothing to clear otherwise).
  if (model.currentId !== null) {
    const disable = el('button', { class: 'lc-btn lc-disable', type: 'button' }, t('ui-logchannel-disable'))
    disable.addEventListener('click', () => handlers.onDisable())
    controls.append(disable)
  }
  root.append(el('div', { class: 'sec-group' }, el('div', { class: 'row lc-row' }, controls)))
}
